import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Editable from "../components/Editable";
import ApoieButton from "../components/ApoieButton/ApoieButton";
import PageHero from "../components/PageHero/PageHero";
import FormActions from "../components/Admin/FormActions/FormActions";
import { SuccessDialog } from "../components/Admin/Dialogs";
import { useAdminPage } from "../hooks/useAdminPage";
import blogHeroImage from "../assets/images/blog-hero.png";
import "./Transparencia.css";

const Transparencia = () => {
  const [page, setPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [deletedItemIds, setDeletedItemIds] = useState([]);

  const adminPage = useAdminPage();
  const navigate = useNavigate();
  const statute = Array.isArray(page?.statute) ? page.statute : [];
  const annualReports = Array.isArray(page?.annualReports) ? page.annualReports : [];
  const financialStatements = Array.isArray(page?.financialStatements)
    ? page.financialStatements
    : [];

  useEffect(() => {
    let isMounted = true;

    async function loadPage() {
      try {
        const response = await api.get("/transparencia");

        if (!isMounted) {
          return;
        }

        setPage(response.data);
      } catch (error) {
        if (isMounted) {
          setPage(null);
        }
      }
    }

    loadPage();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleEditClick = () => {
    if (editMode) {
      setPage(prevPage);
      setError(null);
    } else setPrevPage(structuredClone(page));
    setEditMode((prev) => !prev);
  };

  const toggleDelete = (id) => {
    setDeletedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    try {
      const allDocs = [
        ...(page.statute || []),
        ...(page.annualReports || []),
        ...(page.financialStatements || []),
      ];
      const toDelete = allDocs.filter((item) =>
        deletedItemIds.includes(item._id)
      );
  
      await Promise.all(
        toDelete.map((item) =>
          api.delete(`/transparencia/admin/documentos/${item._id}`)
        )
      );
  
      await api.put("/transparencia/admin", {
        title: String(page?.title || "").trim(),
        introduction: String(page?.introduction || "").trim(),
      });
  
      const response = await api.get("/transparencia");
      setPage(response.data);
      setDeletedItemIds([]);
      setEditMode(false);
      setError(null);
      setSuccessOpen(true);
    } catch (error) {
      setError(error);
    }
  };

  const renderTrashIcon = (id) => {
    if (!editMode) return null;
    const isDeleted = deletedItemIds.includes(id);

    return (
      <button
        className="transparencia-archives__icon"
        onClick={() => toggleDelete(id)}
        title={isDeleted ? "Desfazer exclusão" : "Marcar para excluir"}
        style={{ marginLeft: '8px', cursor: 'pointer', background: 'none', border: 'none' }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke={isDeleted ? "red" : "currentColor"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </button>
    );
  };

  const FileTextIcon = () => (
    <svg
      style={{ flexShrink: 0, minWidth: '24px' }} // <- Esta linha protege o ícone
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  );

  const OpenInNewIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
      <polyline points="15 3 21 3 21 9"></polyline>
      <line x1="10" y1="14" x2="21" y2="3"></line>
    </svg>
  );

  return (
    <>
    {adminPage ? (
      <header className="admin-transparencia__header">
        <h1>EDITAR SEÇÃO</h1>
        <p>Transparência</p>
      </header>
    ) : (
      <>
        <PageHero
          imageUrl={blogHeroImage}
          imageShape="provided"
          title="Transparência"
        />
        <ApoieButton />
      </>
    )}
    <main className={`transparencia-page${adminPage ? " transparencia-page--admin" : ""}`}>
      <Editable
        active={(editMode && adminPage)}
        view={<h2>{page?.title}</h2>}
        edit={
          <input
            className="transparencia-page-h2__input"
            value={page?.title ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                title: e.target.value,
              })
            }
          />
        }
      />
      <Editable
        active={editMode && adminPage}
        view={<p>{page?.introduction}</p>}
        edit={
          <textarea
            className="transparencia-page-p__textarea"
            value={page?.introduction ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                introduction: e.target.value,
              })
            }
          />
        }
      />

      <section className="transparencia-archives">
        <h2>Estatuto</h2>
        <div className="transparencia-archives__content">
          {statute.length > 0 && (
            <>
              {statute.map((item) => (
                <div
                  key={item._id}
                  className="transparencia-archives__line"
                  style={{ backgroundColor: deletedItemIds.includes(item._id) ? "rgba(255, 0, 0, 0.15)" : "" }}
                >
                  <button className="transparencia-archives-btn transparencia-archives__line-btn">{item?.year}</button>
                  <div className="transparencia-archives__interact">
                    <FileTextIcon />
                    <span>{"Visualizar (Abre em nova aba)"}</span>
                    <button className="transparencia-archives__icon"
                      onClick={() => window.open(item.url, "_blank")}>
                      <OpenInNewIcon />
                    </button>
                    {renderTrashIcon(item._id)}
                  </div>
                  {editMode ? (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => navigate(`/transparencia-file/statute/${item._id}`)}>
                      Editar
                    </button>) : (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => window.open(item.downloadUrl, "_blank")}>
                      Download
                    </button>)}
                </div>
              ))}
            </>
          )}
          {editMode && adminPage &&
            <button className="transparencia-archives-btn" onClick={() => navigate("/transparencia-file/statute")}>
              + Adicionar novo estatuto
            </button>}
        </div>
      </section>

      <section className="transparencia-archives">
        <h2>Relatórios Anuais</h2>
        <div className="transparencia-archives__content">
          {annualReports.length > 0 && (
            <>
              {annualReports.map((item) => (
                <div
                  key={item._id}
                  className="transparencia-archives__line"
                  style={{ backgroundColor: deletedItemIds.includes(item._id) ? "rgba(255, 0, 0, 0.15)" : "" }}
                >
                  <button className="transparencia-archives-btn transparencia-archives__line-btn">{item?.year}</button>
                  <div className="transparencia-archives__interact">
                    <FileTextIcon />
                    <span>{"Visualizar (Abre em nova aba)"}</span>
                    <button className="transparencia-archives__icon"
                      onClick={() => window.open(item.url, "_blank")}>
                      <OpenInNewIcon />
                    </button>
                    {renderTrashIcon(item._id)}
                  </div>
                  {editMode ? (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => navigate(`/transparencia-file/annualReports/${item._id}`)}>
                      Editar
                    </button>) : (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => window.open(item.downloadUrl, "_blank")}>
                      Download
                    </button>)}
                </div>
              ))}
            </>
          )}
          {editMode && adminPage &&
            <button className="transparencia-archives-btn" onClick={() => navigate("/transparencia-file/annualReports")}>
              + Adicionar novo relatório
            </button>}
        </div>
      </section>

      <section className="transparencia-archives">
        <h2>Demonstrações Financeiras</h2>
        <div className="transparencia-archives__content">
          {financialStatements.length > 0 && (
            <>
              {financialStatements.map((item) => (
                <div
                  key={item._id}
                  className="transparencia-archives__line"
                  style={{ backgroundColor: deletedItemIds.includes(item._id) ? "rgba(255, 0, 0, 0.15)" : "" }}
                >
                  <button className="transparencia-archives-btn transparencia-archives__line-btn">{item?.year}</button>
                  <div className="transparencia-archives__interact">
                    <FileTextIcon />
                    <span>{"Visualizar (Abre em nova aba)"}</span>
                    <button className="transparencia-archives__icon"
                      onClick={() => window.open(item.url, "_blank")}>
                      <OpenInNewIcon />
                    </button>
                    {renderTrashIcon(item._id)}
                  </div>
                  {editMode ? (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => navigate(`/transparencia-file/financialStatements/${item._id}`)}>
                      Editar
                    </button>) : (
                    <button className="transparencia-archives-btn transparencia-archives__line-btn"
                      onClick={() => window.open(item.downloadUrl, "_blank")}>
                      Download
                    </button>)}
                </div>
              ))}
            </>
          )}
          {editMode && adminPage &&
            <button className="transparencia-archives-btn" onClick={() => navigate("/transparencia-file/financialStatements")}>
              + Adicionar nova demonstração
            </button>}
        </div>
      </section>
      {adminPage && (<div className="transparencia-page-admin">
        {error && <span className="error-message">{error.response?.data?.error || error.error ||
          "Ocorreu um erro, verifique os campos"}</span>}
        <FormActions
          editing={editMode}
          onEdit={handleEditClick}
          onCancel={handleEditClick}
          onSave={handleSave}
        />
      </div>)}
      <SuccessDialog
        open={successOpen}
        message="Alterações salvas com sucesso!"
        onClose={() => setSuccessOpen(false)}
      />
    </main>
    </>
  );
};

export default Transparencia;
